import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../components/Box";
import { Helpers } from "../../../helpers/Helpers";
import { KTCard } from "../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../_metronic/layout/core";
import { storeActivityIndicator } from "../../../redux/reducers/authSlice";
import CustomNumberInput from "../../../components/CustomNumberInput";
import CustomTextInput from "../../../components/CustomTextInput";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomMultiSelect, { CustomMultiSelectModel } from "../../../components/CustomMultiSelect";
import QrNoktasiKarti from "../../../components/QrEtiket/QrNoktasiKarti";
import { MesaiNoktasiItem, MesaiNoktasiPoSatiri, MesaiNoktasiService, NOKTA_TURLERI } from "../../../services/MesaiNoktasiService";

/** Bölüm seçeneğinin değeri: "müşteri|bölüm" (aynı bölüm adı birden çok müşteride olabilir). */
const bolumDegeri = (musteriId: string, bolumId: string) => musteriId + "|" + bolumId;

/**
 * QR Noktası kaydı (standart kayıt ekranı, 11.10.2026; Sabit Gelir Dağıtımı kaydıyla aynı kalıp).
 * QR bölümü kayıt kaydedildikten (kodu oluştuktan) sonra görünür.
 *
 * Fatura ataması (Faz 1b, 11.10.2026, Burak): noktanın otomatik ataması (Sabit / Oranlı) kullanılmıyor;
 * bütün Lojistik ek mesai Atanacak Mesailer havuzuna düşer. Kayıt her zaman "Back office atar" (3) olarak gider.
 *
 * Tür (QR Mesai Faz 2, 18.10.2026): Depo'da iki QR (Lojistik + Ambalaj; iş tipi QR'dan gelir), Ofis'te tek QR.
 */
const MesaiNoktasiAdd = () => {
    const navigate = useNavigate();
    const pageName = "İnsan Kaynakları > Tanımlamalar > QR Noktaları"
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();

    const [mesaiNoktasiId, setMesaiNoktasiId] = useState<string | undefined>(undefined);
    const [ad, setAd] = useState<string | undefined>(undefined);
    const [lat, setLat] = useState<string | undefined>(undefined);
    const [lon, setLon] = useState<string | undefined>(undefined);
    const [yaricap, setYaricap] = useState<string | undefined>('500');
    const [isCustomActive, setIsCustomActive] = useState<boolean>(true);
    const [tur, setTur] = useState<number>(1);
    // Kayıttaki (sunucudaki) tür: QR bölümü kaydedilmiş türe göre çizilir; tür değişince kaydedince yenilenir.
    const [kayitliTur, setKayitliTur] = useState<number>(1);
    const [qrKodu, setQrKodu] = useState<string | undefined>(undefined);
    const [qrMetni, setQrMetni] = useState<string | undefined>(undefined);
    const [qrKoduAmbalaj, setQrKoduAmbalaj] = useState<string | undefined>(undefined);
    const [qrMetniAmbalaj, setQrMetniAmbalaj] = useState<string | undefined>(undefined);
    // #507 Mesai Talepleri eşleşmesi: yetkili personel + müşteri / bölüm (Sipariş ekranındaki gibi PO Eşleştirme satırlarından).
    const [yetkililer, setYetkililer] = useState<CustomMultiSelectModel[]>([]);
    const [musteriler, setMusteriler] = useState<CustomMultiSelectModel[]>([]);
    const [bolumler, setBolumler] = useState<CustomMultiSelectModel[]>([]);
    const [poSatirlari, setPoSatirlari] = useState<MesaiNoktasiPoSatiri[] | undefined>(undefined);

    useEffect(() => {
        var id = searchParams.get('id');

        if (id)
            getById(id);
    }, [])

    const doldur = (k: MesaiNoktasiItem) => {
        setMesaiNoktasiId(k.mesaiNoktasiId);
        setAd(k.ad ?? undefined);
        setLat(k.lat != null ? String(k.lat) : undefined);
        setLon(k.lon != null ? String(k.lon) : undefined);
        setYaricap(k.yaricap != null ? String(k.yaricap) : undefined);
        setIsCustomActive(k.isCustomActive);
        setTur(k.tur || 1);
        setKayitliTur(k.tur || 1);
        qrlariDoldur(k);
        setYetkililer((k.yetkiliPersoneller ?? []).map(x => ({ label: x.ad, value: x.personnelId })));
        setMusteriler((k.musteriler ?? []).map(x => ({ label: x.ad ?? '', value: x.musteriId })));
        setBolumler((k.bolumler ?? []).map(x => ({ label: x.ad ?? '', value: bolumDegeri(x.musteriId, x.bolumId) })));
    }

    // PO Eşleştirme satırları bir kez yüklenir; müşteri ve bölüm seçenekleri buradan türetilir.
    const poYukle = async (): Promise<MesaiNoktasiPoSatiri[]> => {
        if (poSatirlari)
            return poSatirlari;
        const r = await MesaiNoktasiService.poSatirlari();
        if (!r.isSuccess)
            throw r.message;
        setPoSatirlari(r.data);
        return r.data;
    }

    const musteriSecenekleri = async () => {
        try {
            const satirlar = await poYukle();
            const harita = new Map<string, string>();
            satirlar.filter(x => x.customerId).forEach(x => harita.set(x.customerId!, x.customerName ?? ''));
            return { isSuccess: true, data: Array.from(harita, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr')) };
        } catch (e: any) { return { isSuccess: false, message: String(e), data: [] }; }
    }

    const bolumSecenekleri = async () => {
        try {
            const satirlar = await poYukle();
            const secili = new Set(musteriler.map(x => x.value));
            const harita = new Map<string, string>();
            satirlar.filter(x => x.customerId && x.divisionId && secili.has(x.customerId))
                .forEach(x => harita.set(bolumDegeri(x.customerId!, x.divisionId!), (x.customerName ?? '') + ' · ' + (x.divisionName ?? '')));
            return { isSuccess: true, data: Array.from(harita, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'tr')) };
        } catch (e: any) { return { isSuccess: false, message: String(e), data: [] }; }
    }

    // Müşteri listeden çıkarılınca o müşterinin bölümleri de çıkar.
    const musterileriDegistir = (liste: CustomMultiSelectModel[]) => {
        setMusteriler(liste);
        const kalan = new Set(liste.map(x => x.value));
        setBolumler(bolumler.filter(b => kalan.has(b.value.split('|')[0])));
    }

    const qrlariDoldur = (k: MesaiNoktasiItem) => {
        setQrKodu(k.qrKodu || undefined);
        setQrMetni(k.qrMetni || undefined);
        setQrKoduAmbalaj(k.qrKoduAmbalaj || undefined);
        setQrMetniAmbalaj(k.qrMetniAmbalaj || undefined);
    }

    const getById = async (_id: string) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await MesaiNoktasiService.getById(_id);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            doldur(response.data);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const latSayi = lat && lat.trim() != '' ? Helpers.toNumber(lat) : undefined;
    const lonSayi = lon && lon.trim() != '' ? Helpers.toNumber(lon) : undefined;

    // ---------------------------------------------------------------- QR

    const qrYenile = async (ambalaj: boolean) => {
        if (!mesaiNoktasiId)
            return;

        try {
            dispatch(storeActivityIndicator(true));

            var response = await MesaiNoktasiService.qrYenile(mesaiNoktasiId, ambalaj);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            qrlariDoldur(response.data);

            toast.success(ambalaj
                ? "Ambalaj QR'ı yenilendi. Yeni etiketi yazdırıp eskisinin yerine asın."
                : "QR yenilendi. Yeni etiketleri yazdırıp eskilerinin yerine asın.");
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    // ---------------------------------------------------------------- Kaydet

    const saveMesaiNoktasi = async () => {
        try {
            if (!ad || ad.trim() == '') {
                toast.error("Ad alanı zorunludur.");
                return;
            }

            if ((latSayi == undefined) != (lonSayi == undefined)) {
                toast.error("Konum için Enlem ve Boylam birlikte girilmelidir.");
                return;
            }

            if (latSayi != undefined && (isNaN(latSayi) || latSayi < -90 || latSayi > 90)) {
                toast.error("Enlem -90 ile 90 arasında olmalıdır.");
                return;
            }

            if (lonSayi != undefined && (isNaN(lonSayi) || lonSayi < -180 || lonSayi > 180)) {
                toast.error("Boylam -180 ile 180 arasında olmalıdır.");
                return;
            }

            var yaricapSayi = Helpers.toNumber(yaricap);

            if (!(yaricapSayi >= 50 && yaricapSayi <= 5000)) {
                toast.error("Yarıçap 50 ile 5000 metre arasında olmalıdır.");
                return;
            }

            var yeniKayit = !mesaiNoktasiId;

            dispatch(storeActivityIndicator(true));

            var response = await MesaiNoktasiService.kaydet({
                mesaiNoktasiId: mesaiNoktasiId ?? null,
                ad: ad.trim(),
                tur: tur,
                lat: latSayi ?? null,
                lon: lonSayi ?? null,
                yaricap: yaricapSayi,
                // Bütün Lojistik ek mesai havuza düşer (Atanacak Mesailer).
                faturaAtamasi: 3,
                companyPoId: null,
                isCustomActive: isCustomActive,
                dagitimlar: [],
                yetkiliPersonelIdleri: yetkililer.map(x => x.value),
                musteriIdleri: musteriler.map(x => x.value),
                bolumler: bolumler.map(x => ({ musteriId: x.value.split('|')[0], bolumId: x.value.split('|')[1] }))
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success("Kayıt başarılı.");

            dispatch(storeActivityIndicator(false));

            if (yeniKayit) {
                // İlk kayıttan sonra ekranda kalınır; QR bölümü görünür, etiket hemen yazdırılabilir.
                doldur(response.data);
                setSearchParams({ id: response.data.mesaiNoktasiId }, { replace: true });
                return;
            }

            navigate(Helpers.getGoBackUrl())
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    <div>
                        <KTCard className="ebbe-card">
                            <KTCardBody>
                                <h4 className="mb-10 text-black">Genel Bilgiler</h4>

                                <div className="row">
                                    <div className="col-md-6">
                                        <CustomTextInput labelText="Ad" isLabelRequired value={ad} setValue={setAd} />
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label required">Tür</label>
                                        <div className="d-flex" style={{ minHeight: 42, alignItems: 'center' }}>
                                            {
                                                NOKTA_TURLERI.map(x =>
                                                    <label key={x.value} className="form-check form-check-custom form-check-solid me-10">
                                                        <input className="form-check-input" type="radio" checked={tur == x.value} onChange={() => setTur(x.value)} />
                                                        <span className="form-check-label fw-bold">{x.label}</span>
                                                    </label>
                                                )
                                            }
                                        </div>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">Aktif</label>

                                        <CustomSwitch isActive={isCustomActive} setIsActive={setIsCustomActive} />
                                    </div>
                                </div>

                                <div className="text-muted fs-7 mt-5">
                                    {
                                        tur == 2
                                            ? "Ofis: tek QR, iş tipi yok. Ofiste yapılan mesai (ek mesai dahil) yalnız puantaja girer; müşteriye gitmez."
                                            : "Depo: iki QR asılır. Lojistik QR'ıyla yapılan ek mesai, uyan ek mesai talebine yerleşir ya da Atanacak Mesailer'e düşer; Ambalaj QR'ı yalnız puantaja girer."
                                    }
                                    {
                                        mesaiNoktasiId && tur != kayitliTur && (
                                            <span className="text-warning">
                                                {' '}Tür değişikliği kaydedilince QR bölümü yenilenir{tur == 2 ? "; Ambalaj QR'ı kaldırılır" : "; Ambalaj QR'ı üretilir"}.
                                            </span>
                                        )
                                    }
                                </div>
                            </KTCardBody>
                        </KTCard>
                    </div>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-10 text-black">Konum</h4>

                            <div className="row">
                                <div className="col-md-3">
                                    <CustomNumberInput labelText="Enlem" value={lat} setValue={setLat} allowNegative />
                                </div>

                                <div className="col-md-3">
                                    <CustomNumberInput labelText="Boylam" value={lon} setValue={setLon} allowNegative />
                                </div>

                                <div className="col-md-3">
                                    <CustomNumberInput labelText="Yarıçap (m)" isLabelRequired value={yaricap} setValue={setYaricap} />
                                </div>

                                <div className="col-md-3 d-flex align-items-end">
                                    {
                                        latSayi != undefined && lonSayi != undefined && (
                                            <a className="btn btn-sm fw-bold btn-light-primary mb-1" href={`https://www.google.com/maps?q=${latSayi},${lonSayi}`} target="_blank" rel="noopener noreferrer">
                                                <i className="fa fa-map-marker-alt me-1" />
                                                Haritada aç
                                            </a>
                                        )
                                    }
                                </div>
                            </div>

                            <div className="text-muted fs-7 mt-5">
                                Konum, personelin okutma anında {tur == 2 ? 'ofise' : 'depoya'} yakın olup olmadığını kontrol etmek için kullanılır. Yarıçap 50 – 5000 metre.
                            </div>
                        </KTCardBody>
                    </KTCard>

                    {
                        tur == 1 && (
                            <KTCard className="ebbe-card mt-10">
                                <KTCardBody>
                                    <h4 className="mb-10 text-black">Mesai Talebi Eşleşmesi</h4>

                                    <div className="row">
                                        <div className="col-md-4">
                                            <CustomMultiSelect
                                                labelText="Yetkili Personeller"
                                                selectedValues={yetkililer}
                                                onChange={setYetkililer}
                                                onMenuOpen={async () => {
                                                    const r = await MesaiNoktasiService.personelSecenekleri();
                                                    return { ...r, data: (r.data ?? []).map(x => ({ value: x.personnelId, label: x.ad ?? '' })) };
                                                }}
                                                labelName="label"
                                                valueName="value"
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <CustomMultiSelect labelText="Müşteriler" selectedValues={musteriler} onChange={musterileriDegistir} onMenuOpen={musteriSecenekleri} labelName="label" valueName="value" />
                                        </div>

                                        <div className="col-md-4">
                                            <CustomMultiSelect labelText="Bölümler" disabled={musteriler.length == 0} selectedValues={bolumler} onChange={setBolumler} onMenuOpen={bolumSecenekleri} labelName="label" valueName="value" />
                                        </div>
                                    </div>

                                    <div className="text-muted fs-7 mt-5">
                                        Yetkili personel Mesai Talepleri'nde yalnız yetkili olduğu depoları görür (tek depoda yetkiliyse o depo kendiliğinden gelir); hiçbir depoda yetkisi olmayan bütün depoları görür. QR okutmayı etkilemez.
                                        Müşteri seçilirse talepte yalnız bu müşteriler; bölüm seçilmeyen müşterinin bütün bölümleri seçilebilir. Hiç seçim yoksa talep bugünkü gibi çalışır.
                                    </div>
                                </KTCardBody>
                            </KTCard>
                        )
                    }

                    {
                        mesaiNoktasiId && qrKodu && qrMetni && (
                            <QrNoktasiKarti
                                nokta={{ ad: ad ?? '', tur: kayitliTur, qrKodu: qrKodu, qrMetni: qrMetni, qrKoduAmbalaj: qrKoduAmbalaj ?? null, qrMetniAmbalaj: qrMetniAmbalaj ?? null }}
                                onYenile={qrYenile}
                            />
                        )
                    }

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                <Box>
                                    <button className="btn btn-primary btn-sm" onClick={saveMesaiNoktasi}>Kaydet</button>
                                </Box>
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>
        </React.Fragment>
    )
}

export default MesaiNoktasiAdd;
